import { formatBytes, MAX_BYTES } from './DocumentCapture.js';

/**
 * O que o servidor aceita guardar — a lista inteira, do lado de cá.
 *
 * ## Por que ela existe se `DocumentCapture` já tem uma
 *
 * A de lá é **de propósito** mais estreita: aquele controle captura a FOTO de
 * um documento, passa toda imagem pelo canvas e só conhece imagem e PDF. O
 * anexo de uma demanda é outra coisa — é a planilha que o financeiro mandou, o
 * `.docx` do contrato, o CSV da conferência —, e o servidor aceita os oito
 * formatos daqui desde o primeiro dia.
 *
 * ## E por que ela NÃO comprime nada
 *
 * O anexo mais comum de uma demanda é a captura de tela do erro. Passá-la pelo
 * canvas a 1600px com JPEG 0.8, que é o que a `DocumentCapture` faz e faz bem
 * para uma CNH fotografada, borra exatamente o texto que a pessoa está
 * relatando. Aqui o arquivo grande é **recusado com o motivo**, e quem escolhe
 * o que fazer com ele é quem o tem na mão.
 *
 * ## A cópia é declarada, e há um teste segurando as duas pontas
 *
 * `packages/ui` não depende da API e não deve depender. Quem impede a
 * divergência silenciosa é `files.test.ts`, que lê
 * `apps/api/src/common/storage.service.ts` do disco e compara a lista inteira —
 * nos dois sentidos. Um formato que o servidor passe a aceitar e este arquivo
 * não conheça vira um arquivo recusado por uma tela que não precisava recusar.
 */
export const ACCEPTED_TYPES: Record<
  string,
  { extensions: string[]; label: string; kind: AttachmentKind }
> = {
  'application/pdf': { extensions: ['pdf'], label: 'PDF', kind: 'pdf' },
  'image/png': { extensions: ['png'], label: 'PNG', kind: 'image' },
  'image/jpeg': { extensions: ['jpg', 'jpeg'], label: 'JPG', kind: 'image' },
  'image/webp': { extensions: ['webp'], label: 'WebP', kind: 'image' },
  'text/plain': { extensions: ['txt'], label: 'TXT', kind: 'document' },
  'text/csv': { extensions: ['csv'], label: 'CSV', kind: 'document' },
  'application/json': { extensions: ['json'], label: 'JSON', kind: 'document' },
  'application/msword': { extensions: ['doc'], label: 'DOC', kind: 'document' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    extensions: ['docx'],
    label: 'DOCX',
    kind: 'document',
  },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    extensions: ['xlsx'],
    label: 'XLSX',
    kind: 'document',
  },
  'video/mp4': { extensions: ['mp4'], label: 'MP4', kind: 'video' },
  'video/quicktime': { extensions: ['mov'], label: 'MOV', kind: 'video' },
  'audio/mpeg': { extensions: ['mp3'], label: 'MP3', kind: 'audio' },
};

/**
 * Como o anexo se MOSTRA — e é por isso que ele não é o tipo MIME.
 *
 * A grade de anexos precisa responder uma pergunta só: o que desenho neste
 * quadrado, e o que abro quando alguém clicar. `image/png` e `image/webp` têm a
 * mesma resposta; `text/csv` e `application/msword` também, e ela não é "nada"
 * — é o ladrilho do documento com o nome do arquivo, que é o máximo honesto que
 * um navegador consegue prever de um `.docx` sem baixá-lo.
 *
 * Um tipo novo não compila sem escolher um destes cinco, e essa é a intenção: o
 * caso que a grade não sabe desenhar é o que aparece como quadrado vazio
 * esperando um carregamento que nunca vem.
 */
export type AttachmentKind = 'image' | 'pdf' | 'video' | 'audio' | 'document';

/**
 * O gênero do anexo, pelo tipo resolvido — `document` para o que não se
 * reconhece.
 *
 * O padrão é `document` e não um lançamento de erro porque esta função também
 * atende anexo JÁ GRAVADO, cujo `typeMime` veio do banco: um arquivo que subiu
 * quando a allowlist era outra continua tendo que aparecer na lista. Ele
 * aparece como documento, que é verdade sobre ele.
 */
export function kindOfType(typeMime: string): AttachmentKind {
  const declared = (typeMime.split(';')[0] ?? '').trim().toLowerCase();
  if (!Object.hasOwn(ACCEPTED_TYPES, declared)) return 'document';
  return ACCEPTED_TYPES[declared]!.kind;
}

/**
 * Quantos anexos cabem num envio.
 *
 * Não é regra do servidor — lá o limite é por arquivo, e cada `POST` carrega um
 * só. É regra da TELA: doze ladrilhos é o que a grade mostra sem virar uma
 * parede, e o contador ao lado da frase de formatos (`3/12`) existe para a
 * pessoa ver o teto antes de bater nele. Passar disto é quase sempre um zip
 * disfarçado de doze arquivos.
 */
export const MAX_ATTACHMENTS = 12;

/** Os formatos em prosa, para a frase que a tela mostra antes de alguém errar. */
export function acceptedLabels(): string {
  return Object.values(ACCEPTED_TYPES)
    .map((t) => t.label)
    .join(', ');
}

/**
 * A linha inteira do rodapé do campo: os formatos e o teto, numa frase.
 *
 * Mora aqui e não na tela porque ela é a promessa que o servidor cumpre — a
 * lista sai da mesma allowlist e o teto do mesmo `MAX_BYTES` que a recusa usa.
 * Escrita à mão em cada tela, ela envelhece no dia em que um formato entra, e
 * quatro entraram de uma vez em 17/09: uma cópia que não soubesse do MP3 faria
 * a tela recusar em prosa o que o servidor aceita, e quem tentou anexar o áudio
 * lê que ele não serve e desiste.
 */
export function acceptedSentence(): string {
  return `Formatos aceitos: ${acceptedLabels()} · até ${formatBytes(MAX_BYTES)} por arquivo`;
}

/**
 * O valor do atributo `accept` — os tipos **e** as extensões.
 *
 * Os dois, e não só os tipos: o seletor de arquivo do Windows filtra por
 * extensão, e `text/csv` sozinho deixa um `.csv` do Excel fora da lista. É o
 * mesmo motivo de `typeOfAttachment` existir, visto do outro lado.
 */
export function acceptAttribute(only?: readonly string[]): string {
  return Object.entries(ACCEPTED_TYPES)
    /* `only` estreita a lista da casa — nunca a alarga: tipo fora dela não entra. */
    .filter(([tipo]) => !only || only.includes(tipo))
    .flatMap(([tipo, { extensions }]) => [tipo, ...extensions.map((e) => `.${e}`)])
    .join(',');
}

/**
 * O tipo que vale, resolvido pela extensão quando o aparelho erra ou cala.
 *
 * O `type` de um `File` é palpite do sistema operacional, e ele erra de um jeito
 * específico: um `.csv` salvo pelo Excel chega como
 * `application/vnd.ms-excel` no Windows, e parte dos Android manda string
 * **vazia** para tudo. Nos dois casos a extensão sabe mais.
 *
 * Isso não é um buraco de segurança porque não é aqui que se decide: o servidor
 * confere a ASSINATURA do conteúdo contra o tipo declarado. Renomear um `.exe`
 * para `.pdf` passa por esta função e morre lá.
 */
export function typeOfAttachment(typeMime: string, nome: string): string {
  const declared = typeMime.toLowerCase();
  /*
   * `Object.hasOwn` e não `ACCEPTED_TYPES[declared]`: o valor vem do `type` de
   * um `File`, que é palpite do sistema operacional e não passa por validação
   * nenhuma antes de chegar aqui. Com a indexação direta, `"constructor"`,
   * `"__proto__"` e `"tostring"` devolvem membros herdados de
   * `Object.prototype` — todos verdadeiros —, e a função responderia que
   * `constructor` é um tipo aceito, com o arquivo subindo sob um MIME que não
   * existe. Aqui a pergunta é "esta chave é NOSSA", que é a que se queria fazer.
   */
  if (Object.hasOwn(ACCEPTED_TYPES, declared)) return declared;

  const extension = /\.([a-z0-9]+)$/i.exec(nome)?.[1]?.toLowerCase();
  if (!extension) return '';

  const found = Object.entries(ACCEPTED_TYPES).find(([, t]) =>
    t.extensions.includes(extension),
  );
  return found ? found[0] : '';
}

/** O veredito sobre um arquivo escolhido, antes de ele ocupar a rede. */
export type AttachmentVerdict =
  | { ok: true; typeMime: string }
  | { ok: false; reason: string };

/**
 * Se este arquivo pode subir, e quando não pode, por quê.
 *
 * A recusa acontece na ESCOLHA e não no envio. Um `.xlsx` de 40 MB recusado
 * depois de dois minutos de upload numa rede móvel é o defeito que esta função
 * existe para não deixar acontecer — e o servidor continua recusando o mesmo
 * arquivo, porque tela não é proteção.
 */
export function classifyAttachment(
  nome: string,
  typeMime: string,
  sizeBytes: number,
): AttachmentVerdict {
  const tipo = typeOfAttachment(typeMime, nome);
  if (!tipo) {
    return {
      ok: false,
      reason: `${nome} não é um formato aceito. Envie ${acceptedLabels()}.`,
    };
  }

  if (sizeBytes > MAX_BYTES) {
    return {
      ok: false,
      reason:
        `${nome} tem ${formatBytes(sizeBytes)} e o limite é ${formatBytes(MAX_BYTES)}.`,
    };
  }

  // Zero byte passa em toda conferência de tipo e de teto do servidor, e a
  // assinatura de `text/plain` é nula — um arquivo vazio subiria inteiro e
  // apareceria na lista como um anexo de verdade.
  if (sizeBytes === 0) {
    return { ok: false, reason: `${nome} está vazio.` };
  }

  return { ok: true, typeMime: tipo };
}
